import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Fragment,
} from "react";
import Select from "react-select";
import Creatable from "react-select/creatable";

import { useSelector, useDispatch } from "react-redux";
import trimAccents, { toLowercaseAscii } from "util/trimAccents";
import deepEquals from "react-fast-compare";

import optionsSlice from "redux/features/optionData";

import { FaEdit } from "react-icons/fa";

function assembleSearchableChoices(options, input, maxMenuChoices) {
  const inputAscii = toLowercaseAscii(input);
  const matches = options.filter(
    (data) =>
      data.asciiLabel.includes(inputAscii) ||
      data.aliases.some(([description, display, ascii]) =>
        ascii.includes(inputAscii)
      )
  );
  const likelyMatches = matches.filter((data) => !data.unlikely);

  const exactMatches = matches.filter(
    (data) =>
      data.asciiLabel === inputAscii ||
      data.aliases.some(([description, display, ascii]) => ascii === inputAscii)
  );

  const menuTruncated = matches.length > maxMenuChoices;
  const choices = menuTruncated
    ? [...exactMatches]
    : likelyMatches.length > 0
    ? [...likelyMatches]
    : [...matches];

  if (menuTruncated) {
    const numOmitted = matches.length - exactMatches.length;
    choices.push({
      label:
        numOmitted +
        (exactMatches.length > 0 ? " non-exact matches" : " matches") +
        " found -- too many to display -- enter more " +
        "characters from the name to see them",
      value: "-",
      isDisabled: true,
      isNotice: true,
    });

    /* Don't rely on noOptionsMessage because that won't be displayed if there
       is the option to create a new entry; push our own message instead. */
  } else if (choices.length === 0) {
    choices.push({
      label: "No matches found",
      value: "-",
      isDisabled: true,
      isNotice: true,
    });
  }
  return [
    choices,
    {
      truncated: menuTruncated,
      permitCreate: exactMatches.length === 0,
    },
  ];
}

function assembleFixedChoices(options, fixedChoices) {
  const optionsById = Object.fromEntries(
    options.map((option) => [option.value, option])
  );
  const { optionSubset, aliasSelector, extraChoices = [] } = fixedChoices;
  const matches = optionSubset.map((choice) => {
    const option = optionsById[choice.id];
    if (!option) return null;
    return {
      ...option,
      aliases: aliasSelector
        ? option.aliases.filter(
            ([description, display, ascii]) => display === choice[aliasSelector]
          )
        : option.aliases,
    };
  });
  const choices = [...matches.filter((match) => !!match)];
  if (matches.length === 0)
    choices.push({
      id: "-",
      label: "No matches found",
      value: "-",
      isDisabled: true,
      isNotice: true,
    });
  extraChoices.forEach((choice) => {
    choices.push({
      label: choice.label,
      value: choice.value,
      isSpecialAction: true,
    });
  });
  return [choices, {}];
}

function EnhancedSelect(props) {
  const { datasetName, ...other } = props;
  const {
    selector,
    asyncActions: { loader },
  } = optionsSlice(datasetName);
  const optionData = useSelector(selector);
  return (
    <EnhancedSelectInternal
      {...{ optionData, loader, datasetName }}
      {...other}
    />
  );
}

export function EnhancedSelectPreloaded(props) {
  const { options, useFixedChoices, ...other } = props;
  const optionData = {
    loaded: true,
    failed: false,
    loader: null,
    options: options.map((option) => {
      if (option.isDisabled || option.isNotice) return option;
      const { value, labelExtras = [] } = option;
      const [label, asciiLabel] = trimAccents(option.label);
      const aliases = []; // we don't yet use aliases with preloaded data
      return { ...option, value, label, asciiLabel, aliases, labelExtras };
    }),
  };
  const fixedChoices = useFixedChoices
    ? {
        optionSubset: optionData.options.map((option) => {
          return { id: option.value };
        }),
      }
    : undefined;

  return (
    <EnhancedSelectInternal
      {...{ optionData, fixedChoices }}
      loader={null}
      {...other}
    />
  );
}

function EnhancedSelectInternal(props) {
  const {
    datasetName,
    optionData,
    checkOptionUnlikely = () => false,
    loader,
    fixedChoices = null,
    loadConfig,
    onChange = () => {},
    minCharacters = 0,
    creatable = true,
    maxMenuChoices = 100,
    createNewWording = (labelSpan) => <>create new item named {labelSpan}</>,
    selection,
    setSelection,
    showSelectionAsIs = false,
    excludeOptions = {},
    onIncompleteSelection = (inputText) => {},
    keepCrossAsClearIndicator,
    ...other
  } = props;
  const params = { ...other };
  const dispatch = useDispatch();

  const options = optionData.options
    .filter((o) => !excludeOptions[o.value])
    .map((o) => {
      return { ...o, unlikely: checkOptionUnlikely(o) };
    });

  const [input, setInput] = useState("");
  const [value, setValue] = useState(selection ?? null);
  const [menuRequested, setMenuRequested] = useState(false);
  const hasSetSelection = !!setSelection;
  useEffect(() => {
    if (hasSetSelection && !deepEquals(value, selection)) setValue(selection);
  }, [value, selection, hasSetSelection]);

  useEffect(() => {
    if (optionData.loaded == null) dispatch(loader(loadConfig));
  }, [optionData.loaded, dispatch, loader, loadConfig]);

  /* We need to change some undesirable behaviour (bugs?) in the
     internals of react-select, and for that we use a ref to hook in
     to the react-select component itself. For some reason I don't
     fully understand, the component instance is not acccessible as
     ref.current, but as ref.current.select.select (and just
     ref.current.select in newer versions). In case this changes in
     the future, we trap our accesses in a try-catch block and return
     a dummy component so, in such a case, we simply won't get our
     behaviour corrections but there won't be an outright crash.
  */

  const reactSelectRef = useRef(null);
  const reactSelect = () => {
    const dummyRef = {
      blurInput: () => {},
    };
    let ref = dummyRef;
    try {
      ref =
        reactSelectRef.current.select.select || reactSelectRef.current.select;
    } catch (error) {
      console.error(
        "Failed to access react select component",
        error,
        "ref current is",
        reactSelectRef.current
      );
    }
    return ref || dummyRef;
  };

  /* First behaviour change: react-select clears the input text if the user
     closes the menu, or clicks outside to blur the control, without selecting
     anything. We change this behaviour by not incorporating the input change
     in these cases. Possibly in the future we might also preserve the input
     even after a selection, so the same input is put back if the user cancels
     the selection. */

  const handleInputChange = (newInput, { action }) => {
    if (action !== "input-blur" && action !== "menu-close") setInput(newInput);
  };

  /* Second behaviour change: in single-selection mode, react-select hides
      the input box if the user closes the menu via the dropdown, even
      when no value has been selected. We change this behaviour by resetting
      the flag that indicates the request to clear the input box if nothing
      has been selected. In order to know if something has been selected,
      we need to track it in a variable that can mutate in response to
      the selection, rather than waiting until the re-render with the
      new value, at which point it would be too late to block the hiding.

      Not applicable in fixedChoices mode where there is no typing input.
  */

  const valueState = useRef(value);
  // Reset valueState if we adjusted "value" from outside the component
  valueState.current = value;
  const resetInputVisibilityIfNoValueSelected = () => {
    if (!fixedChoices && !valueState.current) {
      const reactSelectObject = reactSelect();

      /*  Older versions of react-select maintain a local variable
            inputIsHiddenAfterUpdate; newer versions access it through
            useState hooks. */

      if (
        reactSelectObject.hasOwnProperty("inputIsHiddenAfterUpdate") &&
        reactSelectObject.inputIsHiddenAfterUpdate
      )
        reactSelectObject.inputIsHiddenAfterUpdate = null;
      else if (reactSelectObject.setState) {
        reactSelectObject.setState((state) => {
          if (state.inputIsHiddenAfterUpdate) {
            return { ...state, inputIsHiddenAfterUpdate: null };
          } else return state;
        });
      } else {
        console.error("Unable to find mechanism for inputIsHiddenAfterUpdate");
      }
    }
  };

  /* Third behaviour change: after a selection, react-select allows
     user to keep typing and put different input into the box. In
     single-selection mode this can result in confusion, as what's showing
     in the box may be different from what's actually selected. Disallow
     this by blurring the input after value selection, and disallowing
     new focus acquisition for as long as a value is selected.

     Not applicable in fixedChoices mode where there is no typing input.
  */

  const blockNewInputIfValueSelected = useCallback(() => {
    if (!fixedChoices && valueState.current && !params.isMulti)
      reactSelect().blurInput();
  }, [fixedChoices, valueState, params.isMulti]);

  const handleValueChange = (newValue) => {
    setValue(newValue);
    if (setSelection) setSelection(newValue);
    onChange(newValue);
    valueState.current = newValue;
    resetInputVisibilityIfNoValueSelected();
    blockNewInputIfValueSelected();
  };

  /* Since we are using this as a controlled component, we already know
     what the user has typed. We therefore don't need to have Select
     do the filtering; we can do it ourselves and pass only the matching
     options. This allows us to implememt fancier things, such as
     limiting the number of displayed items. */

  const dummyFilter = ({ label, value, data }, input) => true;

  const [choices, choicesFeatures] = fixedChoices
    ? assembleFixedChoices(options, fixedChoices)
    : assembleSearchableChoices(options, input, maxMenuChoices);

  const markMatchLocation = (asciiInput, label, asciiLabel) => {
    let start = asciiLabel.indexOf(asciiInput);
    if (start < 0) return null;
    const preMatchAscii = asciiLabel.substring(0, start);

    let preMatch = label.substring(0, start);
    let test = toLowercaseAscii(preMatch);
    while (test.length > preMatchAscii.length) {
      --start;
      preMatch = label.substring(0, start);
      test = toLowercaseAscii(preMatch);
    }
    while (test.length < preMatchAscii.length && test.length < label.length) {
      ++start;
      preMatch = label.substring(0, start);
      test = toLowercaseAscii(preMatch);
    }
    let matchOnward = label.substring(start);

    let end = asciiInput.length;
    let match = matchOnward.substring(0, end);
    test = toLowercaseAscii(match);
    while (test.length > asciiInput.length) {
      --end;
      match = matchOnward.substring(0, end);
      test = toLowercaseAscii(match);
    }
    while (
      test.length < asciiInput.length &&
      test.length < matchOnward.length
    ) {
      ++end;
      match = matchOnward.substring(0, end);
      test = toLowercaseAscii(match);
    }
    return (
      <span>
        {preMatch}
        <span className="has-background-warning">{match}</span>
        {matchOnward.substring(end)}
      </span>
    );
  };

  const displayMatch = (input, data) => {
    const { label, asciiLabel, labelExtras, aliases, isNotice } = data;
    if (isNotice) return <div className="match-notice">{label}</div>;
    if (!asciiLabel) return label;
    const asciiInput = toLowercaseAscii(input);
    const alwaysCheckAliases =
      fixedChoices || (choicesFeatures.truncated && asciiInput !== asciiLabel);

    const labelMatch = alwaysCheckAliases
      ? null
      : markMatchLocation(asciiInput, label, asciiLabel);
    const extras = labelExtras.map((text, index) => (
      <div className="match-label-extra" key={"extra-" + index}>
        {text}
      </div>
    ));
    if (labelMatch)
      return (
        <>
          {labelMatch} {extras}
        </>
      );
    let aliasMatch = null;
    let aliasDescription = null;
    aliases.forEach(([description, display, asciiDisplay]) => {
      if (!aliasMatch) {
        aliasMatch = markMatchLocation(asciiInput, display, asciiDisplay);
        aliasDescription = description;
      }
    });
    return (
      <>
        {label}
        {extras}
        {aliasMatch ? (
          <div className="matched-as" key="alias-match">
            <span className="matched-what">matched {aliasDescription}:</span>{" "}
            {aliasMatch}
          </div>
        ) : (
          ""
        )}
      </>
    );
  };

  const displayCreateNew = (value, valueNumber) => (
    <div className="selected-value create-new" key={valueNumber}>
      {createNewWording(<span className="selected-label">{value.value}</span>)}
    </div>
  );

  const displaySelectedValue = (value, valueNumber) => (
    <div className="selected-value existing" key={valueNumber}>
      {valueNumber === 0 && !props.isMulti && !showSelectionAsIs
        ? "selected "
        : valueNumber > 0
        ? ", "
        : ""}
      <span className="selected-label">{value.label}</span>
    </div>
  );
  const displaySelectedChoice = (value, valueNumber) => (
    <div className="selected-choice" key={valueNumber}>
      <span className="selected-label">
        {value.selectedLabel || value.label}
      </span>
    </div>
  );

  const displaySpecialAction = (value, valueNumber) => (
    <div className="selected-value action" key={valueNumber}>
      <span className="selected-label">
        {value.selectedLabel || value.label}
      </span>
    </div>
  );

  const displaySelected = (values) => (
    <>
      {values.map((value, valueNumber) =>
        value.isSpecialAction
          ? displaySpecialAction(value, valueNumber)
          : value.__isNew__
          ? displayCreateNew(value, valueNumber)
          : fixedChoices
          ? displaySelectedChoice(value, valueNumber)
          : displaySelectedValue(value, valueNumber)
      )}
    </>
  );

  const Component = creatable ? Creatable : Select;

  const EditIndicator = (props) => {
    const {
      children = <Fragment>Change</Fragment>,
      getStyles,
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <div
        {...restInnerProps}
        className="enhancedSelect__clear-indicator"
        ref={ref}
        style={{
          ...getStyles("clearIndicator", props),
          color: "black",
          cursor: "pointer",
        }}
      >
        <div style={{ padding: "0px 5px" }}>
          <FaEdit /> {children}
        </div>
      </div>
    );
  };

  const customSubcomponents = {};
  if (!keepCrossAsClearIndicator)
    customSubcomponents.ClearIndicator = EditIndicator;

  const [justFocused, setJustFocused] = useState(false);
  useEffect(() => {
    if (justFocused) {
      blockNewInputIfValueSelected();
      setJustFocused(false);
    }
  }, [justFocused, blockNewInputIfValueSelected]);
  return (
    <Component
      ref={reactSelectRef}
      filterOption={dummyFilter}
      options={choices}
      components={customSubcomponents}
      value={value}
      {...(fixedChoices
        ? { inputValue: "", isSearchable: false }
        : { inputValue: input, onInputChange: handleInputChange })}
      onChange={handleValueChange}
      onFocus={(event) => setJustFocused(true)}
      onMenuOpen={() => setMenuRequested(true)}
      onMenuClose={() => {
        resetInputVisibilityIfNoValueSelected();
        setMenuRequested(false);
      }}
      menuIsOpen={menuRequested && input.length >= minCharacters}
      isValidNewOption={(inputValue, selectValue, selectOptions) =>
        optionData.loaded &&
        inputValue === inputValue.trim() &&
        !fixedChoices &&
        !choicesFeatures.truncated
      }
      backspaceRemovesValue
      classNamePrefix="enhancedSelect"
      escapeClearsValue
      isClearable={fixedChoices ? undefined : true}
      formatOptionLabel={(data, { inputValue, selectValue, context }) => {
        return context === "menu"
          ? displayMatch(inputValue, data)
          : context === "value"
          ? displaySelected(selectValue)
          : data.label;
      }}
      formatCreateLabel={(inputValue) =>
        createNewWording('"' + inputValue + '"')
      }
      noOptionsMessage={() =>
        optionData.loaded ? (
          "No matches found"
        ) : optionData.failed ? (
          <span className="has-text-danger">
            Error loading {datasetName}: {optionData.failed.message}
          </span>
        ) : (
          <span className="is-italic">Loading...</span>
        )
      }
      placeholder={
        props.placeholder ||
        (fixedChoices
          ? "Please select"
          : minCharacters > 0
          ? "Click and start typing to search ..."
          : "Click to select or type to search ...")
      }
      onBlur={
        params.onBlur ??
        (() => {
          if (!selection) onIncompleteSelection(input);
        })
      }
      {...params}
    />
  );
}

export default EnhancedSelect;
